import React, { useEffect, useState, useContext } from 'react'
import { auth } from '../firebase'
import useDoc from '../hooks/useDoc'
import useCollection from '../hooks/useCollection'
import useAlgolia from 'use-algolia'

import { signOut } from 'views/AuthViews/utils'
import { cloudFunction, CLOUD_FUNCTIONS } from 'firebase/callables'

interface IAppProviderProps {
  children: React.ReactNode
}
export enum hubModes {
  phaseOne = 'PhaseOne',
  phaseTwo = 'PhaseTwo',
  unFunded = 'Unfunded',
  internal = 'Internal',
  studentViewOnly = 'StudentViewOnly',
  student = 'student',
}
interface IAppContextInterface {
  algoliaKeys: any
  currentUser: firebase.User | null | undefined
  userDocState: any | null | undefined
  updateUserDoc?: (data: any) => void
  cohortDoc: any | null | undefined
  foundersCollection: any
  hubMode: hubModes | undefined
  cohortCollectionState: any | undefined
}

export const AppContext = React.createContext<IAppContextInterface>({
  algoliaKeys: undefined,
  foundersCollection: [],
  currentUser: undefined,
  userDocState: undefined,
  updateUserDoc: undefined,
  cohortDoc: undefined,
  hubMode: undefined,
  cohortCollectionState: undefined,
})

export const useAppContext = () => useContext(AppContext)

export const AppProvider: React.FC<IAppProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<
    null | undefined | firebase.User
  >()
  const [userDocState, userDocDispatch, updateUserDoc] = useDoc({})
  const [hubMode, setHubMode] = useState<hubModes | undefined>()
  const [cohortCollectionState, cohortDispatch] = useCollection({})
  const [algoliaKeys, setAlgoliaKeys] = useState<any>({})
  const [algoliaState, requestDispatch, , setAlgoliaConfig] = useAlgolia(
    process.env.REACT_APP_ALGOLIA_APP_ID!,
    '',
    'founders',
    {
      filters: 'isInternal:false',

      hitsPerPage: 250,
    }
  )
  const foundersCollection = {
    algoliaState,
    documents: algoliaState.hits.map(hit => ({ ...hit, id: hit.objectID })),
    requestDispatch,
  }

  useEffect(() => {
    auth.onAuthStateChanged(auth => {
      auth?.getIdTokenResult(true).then(results => {
        const roles = results.claims.roles
        if (!roles || roles.length === 0) {
          signOut()
        } else {
          if (roles.includes('STUDENT')) {
            userDocDispatch({ path: `students/${auth.uid}` })
            setHubMode(hubModes.studentViewOnly)
          } else if (roles.includes('ADVISOR')) {
            userDocDispatch({ path: `advisors/${auth.uid}` })
          } else {
            userDocDispatch({ path: `employees/${auth.uid}` })
            setHubMode(hubModes.internal)
          }
        }
      })
      setCurrentUser(auth)
      if (auth) {
        window.analytics.identify(auth.uid, {
          name: auth.displayName,
          email: auth.email,
        })
      }
    })
  }, [])

  const handleAlgoliaKeys = data => {
    setAlgoliaKeys(
      data.reduce((acc, curr) => {
        return {
          ...acc,
          ...curr.indices.reduce(
            (accIndices, currIndex) => ({
              ...accIndices,
              [currIndex]: curr.key,
            }),
            {}
          ),
        }
      }, {})
    )
  }
  useEffect(() => {
    if (userDocState.doc) {
      if (userDocState.doc && userDocState.doc.algoliaKeys) {
        // console.log(userDocState.doc.algoliaKeys)
        handleAlgoliaKeys(userDocState.doc.algoliaKeys.keys)
      } else {
        // console.log(
        //   `calling ${CLOUD_FUNCTIONS.getAlgoliaKeys}:${
        //     userDocState.path.split('/')[0]
        //   }`
        // )
        cloudFunction(
          CLOUD_FUNCTIONS.getAlgoliaKeys,
          {
            userGroup: userDocState.path.split('/')[0],
          },
          resp => {
            //console.log(resp.data)
            // handleAlgoliaKeys(resp.data.data)
          },
          error => console.log(error)
        )
      }
      // console.log({ userDoc: userDocState.doc })
    }
  }, [userDocState.doc])

  useEffect(() => {
    const { doc, path } = userDocState
    const cohortDoc = cohortCollectionState.documents[0]
    if (doc !== null && cohortDoc) {
      if (path.includes('founders')) {
        if (doc.isFunded) {
          setHubMode(hubModes.phaseTwo)
        } else {
          if (cohortDoc.icResultsDate.toDate() < new Date()) {
            setHubMode(hubModes.unFunded)
          } else {
            setHubMode(hubModes.phaseOne)
          }
        }
      } else if (path.includes('students') && doc.publicProfile) {
        setHubMode(hubModes.student)
      }
    }
  }, [userDocState.doc, cohortCollectionState.documents])

  useEffect(() => {
    if (algoliaKeys.founders) {
      setAlgoliaConfig({
        searchKey: algoliaKeys.founders,
      })
    }
  }, [algoliaKeys])
  useEffect(() => {
    if (userDocState.doc && userDocState.doc.cohort) {
      cohortDispatch({
        path: 'cohorts',
        filters: [
          {
            value: userDocState.doc.cohort,
            operator: '==',
            field: 'cohort',
          },
        ],
      })
    }
  }, [userDocState.doc])
  return (
    <AppContext.Provider
      value={{
        hubMode,
        foundersCollection,
        currentUser,
        userDocState,
        updateUserDoc,
        algoliaKeys,
        cohortDoc: cohortCollectionState.documents[0]
          ? cohortCollectionState.documents[0]
          : null,
        cohortCollectionState,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const tokenHasValue = async (key: string, value: unknown) => {
  const user = auth.currentUser
  if (user) {
    const token = await user.getIdTokenResult()
    if (token.claims[key] && !token.claims[key].includes(value)) {
      user.getIdTokenResult(true)
    }
  }
}

export const refreshToken = () => {
  const user = auth.currentUser
  if (user) user.getIdTokenResult(true)
}
